/* eslint-disable max-lines -- shared dictate control; preferInlineListening (HEI2-71) nests Tauri/web listening UI beside the existing web/desktop paths. */
import { useSessionRecordingStore } from '@/lib/audio/useSessionRecordingStore/useSessionRecordingStore';
import { cn } from '@/lib/cn';
import {
  listenDictationResult,
  listenHandsfreeStart,
  startProgrammaticDictation,
  stopNativeDictation,
} from '@/tauri/dictation';
import { isTauri } from '@/tauri/utils';
import { widgetHide } from '@/tauri/widget';
import { TrackingEventName, track } from '@heidi/analytics/events';
import { ButtonAction } from '@heidi/analytics/vocabulary';
import { Bars } from '@heidi/capture';
import { Button, type ButtonSize, type ButtonVariant } from '@heidi/design-system/button';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { useDictationStore } from '@heidi/dictate';
import { useTranslations } from '@heidi/runtime/i18n';
import { Loader } from '@heidi/snowflake-ui/loader';
import { type Editor as TiptapEditor } from '@tiptap/react';
import { Mic, Square } from 'lucide-react';
import {
  type ReactNode,
  type RefObject,
  memo,
  useCallback,
  useEffect,
  useRef,
} from 'react';

interface DictateButtonProps {
  disabled?: boolean;
  className?: string;
  isMobile?: boolean;
  size?: ButtonSize;
  variant?: ButtonVariant;
  editor?: TiptapEditor | null;
  getEditor?: () => TiptapEditor | null;
  inputRef?: RefObject<HTMLInputElement | HTMLTextAreaElement>;
  idleTooltipContent?: ReactNode;
  onDictationSuccess?: () => void;
  /** Optional hook for parent surfaces to emit composer-specific analytics. */
  onDictateClick?: () => void;
  /**
   * Keep listening feedback in the button (Bars / stop) and hide the floating
   * dictation chrome — Work Home composer (HEI2-71) must not put a mid-window
   * overlay over the page while dictating from the mic slot.
   */
  preferInlineListening?: boolean;
}

/**
 * Shared dictation button used in ChatInput, FloatingAskHeidi, EvidenceNoteOverlay,
 * and the TemplateBuilder dictation surface.
 *
 * Web: drives the JS recording pipeline via `useDictationStore.start()` and inserts
 * text into the focused editor.
 *
 * Desktop (SCR-4525): invokes the native Rust dictation pipeline via
 * `startProgrammaticDictation()`. Rust emits `dictation:handsfree-start` with
 * `triggeredBy: 'button_click_evidence'`, the widget shows, and clipboard-paste
 * lands transcribed text into whichever field has OS focus (the editor is
 * focused before invoking).
 */

const DictateButton = ({
  disabled,
  className,
  isMobile,
  size,
  variant = 'ghost',
  editor,
  getEditor,
  inputRef,
  idleTooltipContent,
  onDictationSuccess,
  onDictateClick,
  preferInlineListening = false,
}: DictateButtonProps) => {
  const { t } = useTranslations();
  const start = useDictationStore((s) => s.start);
  const stop = useDictationStore((s) => s.stop);
  const status = useDictationStore((s) => s.status);
  const text = useDictationStore((s) => s.text);
  const reset = useDictationStore((s) => s.reset);
  const setEditor = useDictationStore((s) => s.setEditor);
  const setDictationMode = useDictationStore((s) => s.setDictationMode);
  const setSource = useDictationStore((s) => s.setSource);
  const source = useDictationStore((s) => s.source);
  const nativeDictationActive = useDictationStore((s) => s.nativeDictationActive);

  const isRecordingSession = useSessionRecordingStore((s) => s.status) === 'RECORDING';

  const isListening = status === 'LISTENING' && source === 'webDictateButton';
  const isProcessing = status === 'PROCESSING' && source === 'webDictateButton';
  const isActive = isListening || isProcessing;

  const startedFromButtonRef = useRef(false);
  const prevStatusRef = useRef(status);

  useEffect(() => {
    if (!onDictationSuccess || !isTauri()) return;

    let disposed = false;
    let removeListeners = () => {};
    void Promise.all([
      listenHandsfreeStart(({ payload }) => {
        if (
          startedFromButtonRef.current &&
          (!payload.recordingStarted || payload.triggeredBy !== 'button_click_evidence')
        ) {
          startedFromButtonRef.current = false;
        }
      }),
      listenDictationResult(({ payload }) => {
        if (!startedFromButtonRef.current) return;
        startedFromButtonRef.current = false;
        if (payload.result.type === 'success' && payload.result.text.trim()) {
          onDictationSuccess();
        }
      }),
    ]).then(
      (unlisteners) => {
        if (disposed) unlisteners.forEach((unlisten) => unlisten());
        else removeListeners = () => unlisteners.forEach((unlisten) => unlisten());
      },
      () => undefined
    );

    return () => {
      disposed = true;
      removeListeners();
    };
  }, [onDictationSuccess]);

  const resolveEditor = useCallback(
    () => getEditor?.() ?? editor ?? null,
    [editor, getEditor]
  );

  // Web path: insert text into the editor when a button-triggered dictation
  // finishes processing. On desktop, Rust does OS-level clipboard-paste, so
  // skip this entirely (the slice's handleNativeDictationResult is called by
  // useDesktopDictationHotkey on button-triggered Result and clears state).
  useEffect(() => {
    if (isTauri()) {
      prevStatusRef.current = status;
      return;
    }
    if (
      prevStatusRef.current === 'PROCESSING' &&
      status !== 'PROCESSING' &&
      startedFromButtonRef.current &&
      resolveEditor() &&
      text
    ) {
      const activeEditor = resolveEditor();
      if (!activeEditor) return;
      activeEditor.commands.insertContent(text + ' ');
      activeEditor.commands.focus('end');

      reset();
      startedFromButtonRef.current = false;
    }

    if (
      startedFromButtonRef.current &&
      (status === 'ERROR' ||
        status === 'INSERTION_ERROR' ||
        (prevStatusRef.current === 'LISTENING' && status === 'INACTIVE'))
    ) {
      startedFromButtonRef.current = false;
    }

    prevStatusRef.current = status;
  }, [status, text, resolveEditor, reset]);

  const handleClick = useCallback(() => {
    if (isListening) {
      // dictation_completed analytics fires from the slice once transcription
      // + insertion both succeed (source='webDictateButton' is read
      // there and maps triggered_by to 'button_click_evidence'). Don't fire
      // here — that would double-count successful button dictations.
      if (isTauri()) {
        void stopNativeDictation().catch(() => {
          startedFromButtonRef.current = false;
        });
      } else {
        void stop()
          .then((didSucceed) => {
            if (didSucceed) onDictationSuccess?.();
          })
          .finally(() => {
            startedFromButtonRef.current = false;
          });
      }
      return;
    }
    if (isActive || isRecordingSession || nativeDictationActive) return;

    startedFromButtonRef.current = true;

    const activeEditor = resolveEditor();
    setEditor(activeEditor);

    // Focus the text target before starting so transcript insertion attaches
    // to this field (web: DOM-level insertion target; desktop: OS-level paste
    // target captured by Rust on start).
    if (activeEditor) {
      activeEditor.commands.focus();
    } else if (inputRef?.current) {
      inputRef.current.focus();
    }

    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        action: ButtonAction.AskHeidiDictateClicked,
        label: 'dictate',
      },
    });
    onDictateClick?.();

    setDictationMode('HANDSFREE');
    setSource('webDictateButton');

    if (isTauri()) {
      void startProgrammaticDictation()
        .then(() => {
          // Composer (and other preferInline surfaces) keep Bars/stop in the
          // mic control; hide the OS-level widget that would otherwise sit in
          // the middle of the window (HEI2-71).
          if (preferInlineListening) {
            void widgetHide();
          }
        })
        .catch(() => {
          startedFromButtonRef.current = false;
        });
    } else {
      void start();
    }
  }, [
    isListening,
    isActive,
    isRecordingSession,
    nativeDictationActive,
    start,
    stop,
    setEditor,
    setDictationMode,
    setSource,
    resolveEditor,
    inputRef,
    onDictationSuccess,
    onDictateClick,
    preferInlineListening,
  ]);

  const getTooltipText = () => {
    if (isListening) {
      return t('tooltips.stopDictating');
    }
    if (isProcessing) {
      return t('common.processing');
    }
    return t('tooltips.dictate');
  };

  return (
    <div className="flex items-center justify-center">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            data-testid="dictate-button"
            variant={variant}
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            disabled={
              disabled ||
              isProcessing ||
              isRecordingSession ||
              // SCR-4525: another native dictation is in flight (e.g. hotkey-
              // triggered) — block until it finishes. During this button's
              // own LISTENING session, isListening is true (we still want
              // the stop click).
              (nativeDictationActive && !isListening)
            }
            size={size ?? (isMobile ? 'lg' : 'sm')}
            iconOnly
            className={cn('group', className)}
          >
            {isListening ? (
              // Desktop hotkey/widget path: floating widget shows the wav, so
              // the button is stop-only. Inline (composer) and web: Bars with a
              // hover-Square stop hint stay in the control (HEI2-71).
              isTauri() && !preferInlineListening ? (
                <Square fill="currentColor" className="text-text-primary" />
              ) : (
                <>
                  <Square
                    fill="currentColor"
                    className="hidden text-text-primary group-hover:block"
                  />
                  <Bars
                    isMic
                    isTab={false}
                    size="compact"
                    className="group-hover:hidden"
                  />
                </>
              )
            ) : isProcessing ? (
              <Loader />
            ) : (
              <Mic aria-hidden />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {idleTooltipContent && !isActive ? (
            idleTooltipContent
          ) : (
            <span>{getTooltipText()}</span>
          )}
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

export default memo(DictateButton);
